import React, { ReactNode, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { IconName } from '@fortawesome/fontawesome-svg-core';
import ROUTE_PATHS from 'deposit/config/routes/route-paths';

import { ApCardError } from 'common/config/assets';
import { translate } from 'common/config/i18n';
import { cookiesHandler } from 'common/helpers/cookiesHandler';
import { useAppSelector, useCallOnInit, useToast } from 'common/helpers/hooks';
import { buildPathWithParams } from 'common/helpers/url';
import { getCookies, getToken } from 'common/store/global';
import { NotificationSeverity } from 'common/types/notifications';
import { Error, ErrorsPurchases } from 'common/types/services';

import CookiesValidation from '../cookies-validation';
import AlertDialog from '../dialogs/alert-dialog';

export interface ErrorNofiticationContextType {
  useHandleError: (error: Error, customActions?: NotificationAction[]) => void;
  useNotificationHandler: (notification?: NotificationContent) => void;
  useClearNotification: () => void;
  useTriggerToast: () => void;
}

export const ErrorNotificationContext = React.createContext<ErrorNofiticationContextType>(
  {} as ErrorNofiticationContextType,
);

export interface NotificationContent {
  title?: string;
  content: string | ReactNode;
  actions?: NotificationAction[];
  iconName?: IconName;
  severity?: NotificationSeverity;
  noActions?: boolean;
  actionsDescription?: string;
  disabledBackdropClick?: boolean;
}
export interface NotificationAction {
  text: string;
  onClick: () => void;
  color: string;
  variant: string;
  fullWidth?: boolean;
}

const ErrorNotification: React.FC = (props) => {
  const { enqueueToast } = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [error, setError] = useState<Error>();
  const [notification, setNotification] = useState<NotificationContent | undefined>();
  const [customActions, setCustomActions] = useState<NotificationAction[] | undefined>();
  const [showCookiesAlert, setShowCookiesAlert] = useState<boolean>(false);

  const token = useAppSelector(getToken);
  const cookies = useAppSelector(getCookies);

  const errorActions = () => {
    const errorsArray = [];
    if (Object.values(ErrorsPurchases).includes(error?.error_info?.key as ErrorsPurchases)) {
      errorsArray.push(
        {
          text: translate('mobile_change_credit_card'),
          onClick: () => {
            navigate(buildPathWithParams(ROUTE_PATHS.selectCard, { token }));
            setIsOpen(false);
          },
          color: 'secondary',
          variant: 'contained',
        },
        {
          text: translate('mobile_change_payment_method'),
          onClick: () => {
            navigate(buildPathWithParams(ROUTE_PATHS.selectMethod, { token }));
            setIsOpen(false);
          },
          color: 'secondary',
          variant: 'outlined',
        },
      );
    }
    return errorsArray;
  };

  const errorContext = {
    useHandleError: (error: Error, customActions?: NotificationAction[]) => {
      if (customActions) {
        setCustomActions(customActions);
      }
      if (Object.values(ErrorsPurchases).includes(error.error_info?.key as ErrorsPurchases)) {
        setIsOpen(true);
        setError(error);
        return;
      }
      enqueueToast({
        text: translate(error.description ?? 'deposit_invalid_request_desc'),
        severity: NotificationSeverity.ERROR,
      });
      return;
    },
    useNotificationHandler: (notification?: NotificationContent) => {
      if (!!notification) {
        setIsOpen(true);
        setNotification(notification);
      }
    },
    useClearNotification: () => {
      setIsOpen(false);
      setNotification(undefined);
    },
    useTriggerToast: () => {
      enqueueToast;
    },
  };
  const { children } = props;

  const image = (error: Error) => {
    if (Object.values(ErrorsPurchases).includes(error.error_info?.key as ErrorsPurchases)) {
      return ApCardError;
    }

    return undefined;
  };

  useCallOnInit(async () => {
    try {
      await cookiesHandler(setShowCookiesAlert, dispatch, cookies);
    } catch (error) {}
  });

  return (
    <ErrorNotificationContext.Provider value={errorContext}>
      {showCookiesAlert ? <CookiesValidation /> : children}

      {error && (
        <AlertDialog
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          actions={customActions ? customActions : errorActions()}
          img={image(error)}
          title={error.error_info?.title && error.error_info.title}
          content={error.error_info?.message}
        />
      )}
      {notification && (
        <AlertDialog
          isOpen={isOpen}
          title={notification.title}
          content={notification.content}
          contentComponent="div"
          iconName={notification.iconName}
          onClose={!notification.disabledBackdropClick ? () => setIsOpen(false) : undefined}
          actionsDescription={notification.actionsDescription}
          actions={
            notification.noActions
              ? undefined
              : notification.actions
              ? notification.actions
              : [
                  {
                    text: translate('deposit_continue'),
                    onClick: () => {
                      setIsOpen(false);
                    },
                    id: 'byAstroAction',
                    color: 'secondary',
                    variant: 'outlined',
                  },
                ]
          }
        />
      )}
    </ErrorNotificationContext.Provider>
  );
};

export default ErrorNotification;
